/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import SvgIcon from '@/pages/home/components/SvgIcon';
import LinkJump from '@gp/components/LinkJump';
import { Bubble, Menu } from 'tea-component';
import { useLocation, history, getLocale, useIntl } from 'umi';
import { parseCommonUrlInfo, getPathWithoutSearchParams } from '../_utils';
import { IMenuItemType, IMenuListWrapPropsType, OpenedChangeMenuType } from '../type';
import { ENV, LANGUAGE_EN } from '@/pages/home/_config/config';
import NewMarkIcon from '@home/components/NewMarkIcon';
import CommonBubble from '@home/components/CommonBubble';

// 受控的环境，预发布和正式
const controlledEnv = ['pre', 'pro'];
const isControlled = controlledEnv.includes(ENV);
interface IMenuItemPropsType {
  menu: IMenuItemType;
  menuPa: IMenuItemType;
  selected: boolean;
  onClick: (menu: IMenuItemType, menuPa: IMenuItemType) => void;
  className?: string;
  isHyperlink: boolean;
  setBubbleInfo: any;
}

const MenuItem = (props: IMenuItemPropsType) => {
  const {
    menu,
    selected,
    onClick,
    className,
    menuPa,
    isHyperlink,
    setBubbleInfo,
  } = props;

  const menuRef = useRef<HTMLLIElement>(null);
  const handleMouseEnter = () => {
    const menuTitle = menuRef.current?.getElementsByClassName(`js-menu-context-text-${menu?.id}`)[0];
    if (menuTitle && menuTitle?.scrollHeight > menuTitle?.clientHeight) {
      setBubbleInfo({
        menu,
        ele: menuRef.current
      });
    }
  };

  const handleMouseLeave = () => {
    setBubbleInfo(null);
  };

  return (
    <LinkJump url={isHyperlink ? menu.path : ''}>
      <Menu.Item
        ref={menuRef}
        // @ts-ignore
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        selected={selected}
        onClick={() => { onClick(menu, menuPa) }}
        className={`js-menu-common-${menu.id} ${className || ''}`}
        key={menu.id}
        // showNew逻辑开发需调整，此处仅模拟展示，注意类名差异
        title={
          <div className="left-nav-text-hd">
            <span className={`menu-context-text js-menu-context-text-${menu?.id}`}>{menu.text}</span>
            {menu?.showNew && <NewMarkIcon className={`left-nav-notice ${menu?.newMarkType === 1 ? 'dbh-beta-tag' : ''}`} type={menu?.newMarkType} />}
          </div>
        }
        icon={
          <SvgIcon className="left-nav-icon" type={menu.icon || 'default'} />
        }
      />
    </LinkJump >
  );
};

export default (props: IMenuListWrapPropsType) => {
  const {
    isNormal,
    setNormal,
    menuList,
    clickMenu,
    isHyperlink,
    currentPath,
    isShowShrinkMenuTextBubble = false,
  } = props;

  const selectedMenuRef = useRef<IMenuItemType | null>(null);
  const selectedMainMenuRef = useRef<IMenuItemType | null>(null);
  // 父父级菜单
  const selectedGrandMenuRef = useRef<IMenuItemType | null>(null);
  const location = useLocation();
  const [bubbleInfo, setBubbleInfo] = useState<null | { ele: HTMLLIElement; menu: any; }>(null);
  // 当前折叠的菜单
  const [openedChangeMenuCur, setOpenedChangeMenuCur] = useState<OpenedChangeMenuType>({
    menuId: '',
    opened: false,
  });
  useIntl();
  const isEn = getLocale() === LANGUAGE_EN;

  const handleChangeMenu = (menu: IMenuItemType, menuPa: IMenuItemType) => {
    const { system } = parseCommonUrlInfo(location.pathname);
    // if (selectedMenuRef.current?.id === menu.id) {
    //   return;
    // }
    if (clickMenu instanceof Function) {
      // 如果 clickMenu 返回 false，直接返回以中止后续代码执行
      if (clickMenu(menu, menuPa) === false) {
        return;
      }
    }
    // 记录点击的menu
    selectedMenuRef.current = menu;
    selectedMainMenuRef.current = menuPa;
    // 修改路由
    history.push(`/${system}/${menu.path || ''}`);
  };

  // 递归筛选可显示的菜单
  const getFilterMenuList = (menuList: IMenuItemType[]) => {
    const filterMenuListNew: IMenuItemType[] = [];
    menuList?.forEach((menu) => {
      const { env = [], subMenus = [] } = menu;
      if (isControlled && !env.includes(ENV)) {
        return;
      }
      const filterSubMenus = subMenus.filter((subMenu) => {
        const { env = [] } = subMenu;
        return !isControlled || env.includes(ENV);
      });
      filterMenuListNew.push({
        ...menu,
        subMenus: getFilterMenuList(filterSubMenus),
      });
    });
    return filterMenuListNew;
  };

  // 筛选可显示的菜单
  const filterMenuList = useMemo((): IMenuItemType[] => {
    return getFilterMenuList(menuList);
  }, [menuList]);

  // 递归赋值系统语言文本
  const getFormatMenuList = (menuList: IMenuItemType[]): IMenuItemType[] => {
    if (!Array.isArray(menuList)) return menuList;
    return menuList?.map((item: IMenuItemType) => {
      const { title, titleCN = '' } = item;
      const itemTemp = {
        ...item,
        text: isEn ? title : titleCN || title,
      };
      if (item.subMenus && Array.isArray(item.subMenus) && item.subMenus.length > 0) {
        return {
          ...itemTemp,
          subMenus: getFormatMenuList(item.subMenus),
        };
      }
      return itemTemp;
    });
  };

  // 切换语言
  const formatMenuList: IMenuItemType[] = useMemo((): IMenuItemType[] => {
    return getFormatMenuList(filterMenuList);
  }, [isEn, filterMenuList]);

  useMemo(() => {
    // todo menuList 变化，重置选择项
    selectedMenuRef.current = null;
    selectedMainMenuRef.current = null;
    selectedGrandMenuRef.current = null;
  }, [filterMenuList]);

  // 根据路由确认左侧菜单选中态
  useMemo(() => {
    const { pathname, state = {} as any } = location;
    let { realPath } = parseCommonUrlInfo(pathname);
    if (currentPath) {
      realPath = currentPath;
    }

    if (!realPath) {
      return;
    }
    // todo 如果路径相同，直接返回
    if (realPath === selectedMenuRef.current?.path) {
      return;
    }

    // todo 处理点击下钻页，左侧菜单保持前一个状态
    if (selectedMenuRef.current && state?.noMatch === true) {
      return;
    }

    // 路由变化时重置上一次选中态，重新计算当前路由对应的选中菜单
    selectedMenuRef.current = null;
    selectedMainMenuRef.current = null;
    selectedGrandMenuRef.current = null;

    // 编码归一化：useLocation().pathname 会把 %20 等 percent-encoded 字符解码回原文，
    // 而 AuthPath 类型 menu.path 是用 encodeURIComponent(title) 拼出来的（带 %20），
    // 两边不归一化会让含空格 / 中文 title 的菜单 startsWith 永远返回 false，刷新后选中态丢失。
    const decodedRealPath = (() => { try { return decodeURIComponent(realPath); } catch { return realPath; } })();
    const matchMenuPath = (menuPath?: string): boolean => {
      if (!menuPath) return false;
      const stripped = getPathWithoutSearchParams(menuPath);
      try { return decodedRealPath.startsWith(decodeURIComponent(stripped)); } catch { return decodedRealPath.startsWith(stripped); }
    };

    // 遍历菜单树，命中后写入选中态 refs，返回是否命中
    const traverseMatchMenu = (isMatch: (menuPath?: string) => boolean): boolean => {
      return filterMenuList?.some(menu => {
        if (menu?.subMenus?.length) {
          return menu.subMenus.some(subMenu => {
            // 三级菜单特殊逻辑，如果浏览器刷新时，选中的是第三级菜单，需要添加三个菜单选中状态
            if (subMenu?.subMenus?.length) {
              return subMenu?.subMenus?.some((sunMenu) => {
                if (isMatch(sunMenu?.path)) {
                  selectedMenuRef.current = sunMenu;
                  selectedMainMenuRef.current = subMenu;
                  selectedGrandMenuRef.current = menu;
                  return true;
                }
                return false;
              });
            }

            if (isMatch(subMenu?.path)) {
              selectedMenuRef.current = subMenu;
              selectedMainMenuRef.current = menu;
              return true;
            }
            return false;
          });
        }

        if (isMatch(menu?.path)) {
          selectedMenuRef.current = menu;
          selectedMainMenuRef.current = menu;
          return true;
        }
        return false;
      });
    };

    if (!traverseMatchMenu(matchMenuPath)) {
      // 详情页等动态路由（如 topCharts/mobileGames/gamesDetails/:id/stats/:segment）
      // 无法通过 startsWith 命中菜单 path（菜单 path 指向榜单落地页），
      // 逐级截短 realPath 做前缀兜底匹配，定位当前路由所属的菜单
      const segments = decodedRealPath.split('/').filter(Boolean);
      let matched = false;
      while (segments.length > 1 && !matched) {
        segments.pop();
        const prefixPath = segments.join('/');
        matched = traverseMatchMenu((menuPath?: string): boolean => {
          if (!menuPath) return false;
          const stripped = getPathWithoutSearchParams(menuPath);
          let decodedStripped = stripped;
          try {
            decodedStripped = decodeURIComponent(stripped);
          } catch {
            /* 保持原值 */
          }
          return decodedStripped === prefixPath || decodedStripped.startsWith(`${prefixPath}/`);
        });
      }
    }
  }, [location, currentPath, filterMenuList]);

  // 菜单定位
  useEffect(() => {
    let { realPath } = parseCommonUrlInfo(location.pathname);
    if (currentPath) {
      realPath = currentPath;
    }
    if (!realPath || !selectedMenuRef.current) {
      return;
    }

    const $menuList: HTMLCollectionOf<Element> = document.getElementsByClassName(`js-menu-common-${selectedMenuRef.current.id}`);
    if ($menuList.length < 1) {
      return;
    }

    // 检查当前菜单是否在视野范围内
    const inWindow = [...$menuList].some((item): boolean => {
      const rectObject = item.getBoundingClientRect();
      const bottomHeight = 42; // 菜单区域下边界与底部的距离
      const topHeight = 50;  // 菜单区域上边界与顶部的距离
      const selfHalfHeight = 18;  // 一个菜单项的半高
      const parentElement = item?.parentElement?.parentElement;
      if (item.classList.contains('menu-context-primary')) {
        if (rectObject?.top <= window.innerHeight - bottomHeight - selfHalfHeight && rectObject?.bottom >= topHeight + selfHalfHeight) {
          return true;
        }
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (parentElement && parentElement.classList.contains('is-expanded') && rectObject?.top <= window.innerHeight - bottomHeight - selfHalfHeight && rectObject?.bottom >= topHeight + selfHalfHeight) {
        return true;
      }
      return false;
    });

    // 不在视野的情况下，滚动到视野中
    if (!inWindow) {
      const $parentElement = $menuList[0]?.parentElement?.parentElement;
      if ($parentElement && !$parentElement.classList.contains('is-expanded')) {
        // @ts-ignore
        $parentElement?.childNodes?.[0].click(); // 模拟点击
      }

      // 三级菜单特殊逻辑，如果有父父级菜单，则要同时打开父父级菜单
      if (selectedGrandMenuRef.current && selectedMainMenuRef.current) {
        const $menuGrandList: HTMLCollectionOf<Element> = document.getElementsByClassName(
          `js-menu-common-submenu-${selectedMainMenuRef.current.id}`,
        );
        if ($menuGrandList) {
          const $parentGrandElement = $menuGrandList[0]?.parentElement?.parentElement;
          if ($parentGrandElement && !$parentGrandElement.classList.contains('is-expanded')) {
            // @ts-ignore
            $parentGrandElement?.childNodes?.[0].click(); // 模拟点击
          }
        }
      }

      // 强制异步执行
      setTimeout(() => {
        $menuList[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 10);
    }
  }, [location, menuList, currentPath]);

  // 三级菜单特殊逻辑，如果当前选中的是第三级菜单，当折叠当前一级菜单时，要选中高亮
  const isSelectedSubMenuCur = useMemo(() => {
    if (
      openedChangeMenuCur?.menuId &&
      !openedChangeMenuCur?.opened &&
      selectedMainMenuRef.current
    ) {
      if (openedChangeMenuCur?.menuId === selectedMainMenuRef.current?.parentMenuId) {
        return true;
      }
      return false;
    }
    return false;
  }, [openedChangeMenuCur]);

  // 多一层菜单级联
  const renderMenu = (
    menuItemList: IMenuItemType[],
    menuPa: IMenuItemType,
    isFirstMenu: boolean = false,
  ) => {
    return menuItemList?.map((menu: IMenuItemType) => {
      if (menu?.subMenus && menu?.subMenus?.length > 0) {
        return (
          <Menu.SubMenu
            defaultOpened={menu.defaultOpened}
            onOpenedChange={(opened) => {
              setOpenedChangeMenuCur({
                menuId: menu.id,
                opened,
              });
            }}
            key={menu.id}
            className={classnames(menu?.className, `js-menu-common-submenu-${menu.id}`, {
              'left-nav-third-level-menu': !isFirstMenu,
              'is-selected': menu.id === openedChangeMenuCur?.menuId && isSelectedSubMenuCur,
            })}
            icon={menu?.isLocalIcon ? <img className="dbh-menu-local-icon" src={menu?.localIcon} alt="" /> : <SvgIcon className="left-nav-icon" type={menu.icon || 'default'} />}
            // showNew逻辑开发需调整，此处仅模拟展示，注意类名差异
            title={
              <div className="left-nav-hd">
                <span className="menu-context-text">{menu.text}</span>
                {menu?.showNew && (
                  <NewMarkIcon
                    className={`left-nav-notice db-parent-menu-new ${
                      menu?.newMarkType === 1 ? 'dbh-beta-tag' : ''
                    }`}
                    type={menu?.newMarkType}
                  />
                )}
              </div>
            }
          >
            {renderMenu(menu.subMenus, menu)}
          </Menu.SubMenu>
        );
      }
      return (
        <MenuItem
          key={menu.id}
          className={
            isFirstMenu
              ? classnames('menu-context-primary', menuPa?.className)
              : menu?.className
          }
          menu={menu}
          menuPa={menuPa}
          onClick={handleChangeMenu}
          selected={selectedMenuRef.current?.id === menu.id}
          isHyperlink={isHyperlink}
          setBubbleInfo={setBubbleInfo}
        />
      );
    });
  };

  return <div className="navmenu-bd">
    {isNormal ? (
      <Menu className="menu-context">{renderMenu(formatMenuList, formatMenuList, true)}</Menu>
    ) : (
      <div className="width-item">
        <div className="width-item-line"></div>
        {formatMenuList.map(menu => (
          <CommonBubble
            content={isShowShrinkMenuTextBubble ? menu.text : null}
            placement='right'
            dark
          >
            <div key={menu.id} className={`width-item-icon ${selectedMainMenuRef.current?.id === menu.id ? "width-item-action" : ""}`} onClick={() => { setNormal(true) }}>
              <SvgIcon type={menu.icon || 'default'} />
            </div>
          </CommonBubble>
        ))}
      </div>
    )}
    {bubbleInfo && <Bubble visible={true} dark placement='right' referenceElement={bubbleInfo.ele} content={bubbleInfo.menu?.text} />}
  </div>;
};
